import { delay, put, takeLatest, select } from 'redux-saga/effects'
import * as Eff from 'redux-saga/effects' // tslint:disable-line:no-duplicate-imports
import toastr from 'toastr'
import axios from 'axios'

import Logger from '@Utils/Logger'
import { warnIfOdubPoolStopped } from '@Utils/OdubPoolStatus'

import {
  IAction,
  ISuccessResponse,
} from '@Root/types'

import {
  fetchSpeakerVoicesSuccess,
  fetchSpeakerVoicesFailure,
  fetchLanguagesSuccess,
  fetchLanguagesFailure,
  createSpeakerVoiceSuccess,
  createSpeakerVoiceFailure,
  updateSpeakerVoiceSuccess,
  updateSpeakerVoiceFailure,
  removeSpeakerVoicesFromWorkspaceSuccess,
  removeSpeakerVoicesFromWorkspaceFailure,
  generateDesignSampleSuccess,
  generateDesignSampleFailure,
} from './actions'

import {
  SpeakerVoiceActionTypes,
  IFetchSpeakerVoicesPayload,
  IFetchSpeakerVoicesSuccessResponseBody,
  IFetchLanguagesSuccessResponseBody,
  ICreateSpeakerVoicePayload,
  ICreateSpeakerVoiceSuccessResponseBody,
  IUpdateSpeakerVoicePayload,
  IUpdateSpeakerVoiceSuccessResponseBody,
  IRemoveSpeakerVoicesFromWorkspacePayload,
  IGenerateDesignSamplePayload,
  IGenerateDesignSampleJobResponseBody,
  IGenerateDesignSampleJobStatusResponseBody,
} from './types'

import {
  getSearchTerm,
  getSelectedLanguagesNames,
} from './selectors'

import * as API from './api'

const call: any = Eff.call
const DEFAULT_GENERATE_SAMPLE_ERROR_MESSAGE = 'We could not generate a sample for this voice description.'
const DESIGN_SAMPLE_COLD_START_MESSAGE = 'Voice creation may take several minutes while the service starts.'
const DESIGN_SAMPLE_POLL_INTERVAL_MS = 2000
const DESIGN_SAMPLE_MAX_POLLS = 180

const formatDesignSampleError = (message: unknown): string => {
  if (typeof message === 'string') {
    const trimmedMessage = message.trim()

    return trimmedMessage || DEFAULT_GENERATE_SAMPLE_ERROR_MESSAGE
  }

  if (Array.isArray(message)) {
    const messages = message
      .filter((entry): entry is string => typeof entry === 'string')
      .map(entry => entry.trim())
      .filter(Boolean)

    return messages.length > 0 ? messages.join(', ') : DEFAULT_GENERATE_SAMPLE_ERROR_MESSAGE
  }

  if (message && typeof message === 'object') {
    const messages = Object.entries(message as Record<string, unknown>).flatMap(([field, value]) => {
      const normalizedField = field.replace(/_/g, ' ')

      if (typeof value === 'string') {
        const trimmedValue = value.trim()
        return trimmedValue ? [`${normalizedField} ${trimmedValue}`] : []
      }

      if (Array.isArray(value)) {
        return value
          .filter((entry): entry is string => typeof entry === 'string')
          .map(entry => entry.trim())
          .filter(Boolean)
          .map(entry => `${normalizedField} ${entry}`)
      }

      return []
    })

    return messages.length > 0 ? messages.join(', ') : DEFAULT_GENERATE_SAMPLE_ERROR_MESSAGE
  }

  return DEFAULT_GENERATE_SAMPLE_ERROR_MESSAGE
}

function* fetchSpeakerVoicesSaga(action: IAction<IFetchSpeakerVoicesPayload>) {
  try {
    const searchTerm: string | undefined = yield select(getSearchTerm)
    const selectedLanguagesNames: string[] = yield select(getSelectedLanguagesNames)
    const payload = {
      ...action.payload,
      ...(searchTerm ? { searchTerm } : {}),
      ...(selectedLanguagesNames.length ? { selectedLanguagesNames } : {}),
    }

    const response: ISuccessResponse<IFetchSpeakerVoicesSuccessResponseBody> = yield call(API.fetch, payload)
    yield put(fetchSpeakerVoicesSuccess(response.data))
  } catch (e) {
    Logger.error(e, 'Speaker voices could not be loaded')
    yield put(fetchSpeakerVoicesFailure())
  }
}

function* fetchLanguagesSaga() {
  try {
    const response: ISuccessResponse<IFetchLanguagesSuccessResponseBody> = yield call(API.fetchLanguages)
    yield put(fetchLanguagesSuccess(response.data))
  } catch (e) {
    Logger.error(e, 'Languages could not be loaded')
    yield put(fetchLanguagesFailure())
  }
}

function* createSpeakerVoiceSaga(action: IAction<ICreateSpeakerVoicePayload>) {
  try {
    const { openAfterCreation, ...apiPayload } = action.payload

    const response: ISuccessResponse<ICreateSpeakerVoiceSuccessResponseBody> = yield call(API.createSpeakerVoice, apiPayload)
    yield put(createSpeakerVoiceSuccess({ ...response.data, openAfterCreation }))
  } catch (err) {
    Logger.error(err, 'Speaker voice could not be created')
    yield put(createSpeakerVoiceFailure())
  }
}

function* updateSpeakerVoiceSaga(action: IAction<IUpdateSpeakerVoicePayload>) {
  try {
    const payload = { ...action.payload, returnSpeakerVoice: true }
    const response: ISuccessResponse<IUpdateSpeakerVoiceSuccessResponseBody> = yield call(API.update, payload)
    yield put(updateSpeakerVoiceSuccess(response.data))
    toastr.success('', 'Voice updated successfully.')
  } catch (e) {
    Logger.error(e, 'Speaker voice could not be updated')
    yield put(updateSpeakerVoiceFailure())
  }
}

function* removeSpeakerVoicesFromWorkspaceSaga(action: IAction<IRemoveSpeakerVoicesFromWorkspacePayload>) {
  try {
    const { preventNotification, ...apiPayload } = action.payload

    yield call(API.removeFromWorkspace, apiPayload)
    yield put(removeSpeakerVoicesFromWorkspaceSuccess({ speakerVoiceIds: apiPayload.speakerVoiceIds }))
    const hasSeveralRemovedSpeakerVoices = apiPayload.speakerVoiceIds.length > 1

    if (preventNotification) { return }

    toastr.success('', hasSeveralRemovedSpeakerVoices ? 'Voices have been removed from your workspace.' : 'Voice has been removed from your workspace.')
  } catch (e) {
    Logger.error(e, 'Speaker voice could not be removed from workspace')
    yield put(removeSpeakerVoicesFromWorkspaceFailure())
  }
}

function* generateDesignSampleSaga(action: IAction<IGenerateDesignSamplePayload>) {
  try {
    yield call(warnIfOdubPoolStopped, {
      workspaceId: action.payload.workspaceId,
      pool: 'voice_design',
      message: DESIGN_SAMPLE_COLD_START_MESSAGE,
      title: 'Preparing voice creation',
      errorContext: 'ODUB Voice Design pool status could not be checked',
    })

    const response: ISuccessResponse<IGenerateDesignSampleJobResponseBody> = yield call(API.generateDesignSample, action.payload)

    for (let poll = 0; poll < DESIGN_SAMPLE_MAX_POLLS; poll += 1) {
      yield delay(DESIGN_SAMPLE_POLL_INTERVAL_MS)
      const statusResponse: ISuccessResponse<IGenerateDesignSampleJobStatusResponseBody> = yield call(
        API.getDesignSampleJob,
        action.payload.workspaceId,
        response.data.jobId
      )

      if (statusResponse.data.status !== 'success') { continue }
      if (!statusResponse.data.audioUrl) { throw new Error('ODUB sample completed without an audio URL') }

      yield put(generateDesignSampleSuccess({
        audioUrl: statusResponse.data.audioUrl,
        durationSeconds: statusResponse.data.durationSeconds,
      }))
      return
    }

    throw new Error('ODUB sample generation timed out')
  } catch (e) {
    Logger.error(e, 'Speaker voice sample could not be generated')
    const payload = axios.isAxiosError(e) ? e.response?.data as { message?: unknown; error?: unknown } | undefined : undefined

    yield put(generateDesignSampleFailure({
      message: formatDesignSampleError(payload?.message || payload?.error)
    }))
  }
}

function* subscriptionSagas() {
  yield takeLatest(SpeakerVoiceActionTypes.FETCH_SPEAKER_VOICES, fetchSpeakerVoicesSaga)
  yield takeLatest(SpeakerVoiceActionTypes.FETCH_LANGUAGES, fetchLanguagesSaga)
  yield takeLatest(SpeakerVoiceActionTypes.CREATE_SPEAKER_VOICE, createSpeakerVoiceSaga)
  yield takeLatest(SpeakerVoiceActionTypes.UPDATE_SPEAKER_VOICE, updateSpeakerVoiceSaga)
  yield takeLatest(SpeakerVoiceActionTypes.REMOVE_SPEAKER_VOICES_FROM_WORKSPACE, removeSpeakerVoicesFromWorkspaceSaga)
  yield takeLatest(SpeakerVoiceActionTypes.GENERATE_DESIGN_SAMPLE, generateDesignSampleSaga)
}

export default subscriptionSagas
